import { useState, useEffect } from 'react';
import { X, Trash2, FolderPlus, Check, Store, Globe, ChefHat, Tablet } from 'lucide-react';
import { api } from '../../lib/spizirnaUi';
import RecipeKatIcon, { ICON_MAP, ICON_KEYS } from './RecipeKatIcon';
import { KAT_PALETTE } from './receptUtils';

const ACCENT = '#F59E0B';

// Modal úpravy/vytvoření kategorie — název, barva, ikona na jednom místě.
// state: { mode: 'create' | 'edit', node?, parentId?, parentNazev? }
// Rodič modal remountuje přes `key`, takže lokální stav se inicializuje z čerstvého `state`.
export default function RecipeCategoryModal({ state, isDark, onClose, onSave, onCreate, onDelete, onAddSub, zobrazitViditelnost = true }) {
    const isEdit = state?.mode === 'edit';
    const node = state?.node;

    const [nazev, setNazev] = useState(isEdit ? (node?.nazev || '') : '');
    const [barva, setBarva] = useState(isEdit ? (node?.barva || null) : null);
    const [ikona, setIkona] = useState(isEdit ? (node?.ikona || null) : null);
    // Viditelnost — kam se kategorie propíše (admin vidí vždy). Nová = jen admin.
    const [visPokladna, setVisPokladna] = useState(isEdit ? !!node?.viditelne_pokladna : false);
    const [visEshop, setVisEshop] = useState(isEdit ? !!node?.viditelne_eshop : false);
    // Kiosek s lamami má vlastní nabídku, oddělenou od pokladny i e-shopu.
    const [visKiosek, setVisKiosek] = useState(isEdit ? !!node?.viditelne_kiosek : false);
    // Kuchyňská stanice (Kuchyně 1, 2, Bar); null = zdědit z nadřazené kategorie.
    const [kdsStanice, setKdsStanice] = useState(isEdit ? (node?.kds_stanice_id ?? null) : null);
    const [stanice, setStanice] = useState([]);
    useEffect(() => {
        if (!zobrazitViditelnost) return;
        api('/api/kds-stanice').then(d => setStanice((d || []).filter(x => x.aktivni))).catch(() => {});
    }, [zobrazitViditelnost]);

    // Escape zavírá
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    if (!state) return null;

    const border = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const muted = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)';
    const fieldBg = isDark ? 'rgba(255,255,255,0.06)' : '#fff';

    const canSave = nazev.trim().length > 0;
    const submit = () => {
        if (!canSave) return;
        const data = { nazev: nazev.trim(), barva, ikona };
        // Bez sekce viditelnosti pole neposíláme — ať skryté ovládání nepřepisuje
        // hodnoty, které v tomhle kontextu uživatel nemá jak vidět ani nastavit.
        if (zobrazitViditelnost) {
            data.viditelne_pokladna = visPokladna;
            data.viditelne_eshop = visEshop;
            data.viditelne_kiosek = visKiosek;
            if (stanice.length > 1) data.kds_stanice_id = kdsStanice;
        }
        if (isEdit) onSave(node.id, data);
        else onCreate(state.parentId ?? null, data);
        onClose();
    };
    const handleDelete = () => {
        if (confirm(`Smazat kategorii „${node.nazev}"? Podkategorie se přesunou o úroveň výš.`)) {
            onDelete(node.id);
            onClose();
        }
    };

    const label = (text) => (
        <label className="block text-[10px] font-semibold uppercase mb-1.5" style={{ color: muted, letterSpacing: '0.05em' }}>{text}</label>
    );
    const autoBtn = (onClick, active) => (
        <button onClick={onClick} title="Automaticky (dle názvu)"
            className="text-[10px] px-2 py-0.5 rounded"
            style={{ color: active ? ACCENT : muted, border: `1px solid ${active ? ACCENT : border}`, background: 'none', cursor: 'pointer', height: 22 }}>
            auto
        </button>
    );

    const title = isEdit ? `Upravit kategorii${node?.nazev ? ` · ${node.nazev}` : ''}` : (state.parentNazev ? 'Nová podkategorie' : 'Nová kategorie');

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={{ width: 460, maxWidth: '95vw', maxHeight: '85vh', borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden',
                background: isDark ? 'rgba(24,22,32,0.92)' : 'rgba(255,255,255,0.94)',
                backdropFilter: 'blur(28px) saturate(1.4)', WebkitBackdropFilter: 'blur(28px) saturate(1.4)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`, boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>

                {/* Header */}
                <div className="flex items-center justify-between p-4" style={{ borderBottom: `1px solid ${border}` }}>
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted }}><X size={18} /></button>
                </div>

                {/* Body */}
                <div className="p-4 overflow-y-auto">
                    {/* Náhled + kontext */}
                    <div className="flex items-center gap-3 mb-4">
                        <RecipeKatIcon slozka ikona={ikona} kategorieNazev={nazev} kategorieBarva={barva} size={46} radius={9} />
                        <div className="min-w-0">
                            <div className="text-sm font-semibold truncate" style={{ color: nazev.trim() ? 'var(--text-primary)' : muted }}>
                                {nazev.trim() || 'Náhled kategorie'}
                            </div>
                            <div className="text-[11px]" style={{ color: muted }}>
                                {state.parentNazev ? `Podkategorie: ${state.parentNazev}` : 'Kořenová kategorie'}
                            </div>
                        </div>
                    </div>

                    {/* Název */}
                    {label('Název')}
                    <input value={nazev} autoFocus onChange={e => setNazev(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') submit(); }}
                        placeholder="Např. Omáčky"
                        className="w-full text-sm rounded-lg px-3 py-2 mb-4"
                        style={{ background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)', outline: 'none' }} />

                    {/* Barva */}
                    {label('Barva')}
                    <div className="flex flex-wrap items-center gap-1.5 mb-4">
                        {KAT_PALETTE.map(c => (
                            <button key={c} onClick={() => setBarva(c)} title={c}
                                style={{ width: 24, height: 24, borderRadius: 5, background: c, cursor: 'pointer',
                                    border: barva === c ? '2px solid var(--text-primary)' : '1px solid rgba(0,0,0,0.15)' }} />
                        ))}
                        {autoBtn(() => setBarva(null), barva == null)}
                    </div>

                    {/* Ikona */}
                    {label('Ikona')}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 6 }}>
                        {ICON_KEYS.map(key => {
                            const Icon = ICON_MAP[key];
                            const on = ikona === key;
                            return (
                                <button key={key} onClick={() => setIkona(key)} title={key}
                                    className="rounded-lg flex items-center justify-center"
                                    style={{ aspectRatio: '1', cursor: 'pointer',
                                        background: on ? 'rgba(245,158,11,0.15)' : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                                        border: `1px solid ${on ? ACCENT : border}` }}>
                                    <Icon size={17} style={{ color: on ? ACCENT : 'var(--text-primary)' }} />
                                </button>
                            );
                        })}
                    </div>
                    <div className="mt-2">{autoBtn(() => setIkona(null), ikona == null)}</div>

                    {/* Viditelnost — pokladna / e-shop (admin vidí vždy).
                        Skladové kategorie (suroviny, obaly, spotřební zboží) se
                        zákazníkovi nikde nenabízejí, takže tam volba nedává smysl. */}
                    {zobrazitViditelnost && (
                    <div className="mt-5">
                        {label('Viditelnost')}
                        <div className="flex flex-col gap-2">
                            {[
                                { on: visPokladna, set: setVisPokladna, Icon: Store, text: 'Pokladna' },
                                { on: visEshop, set: setVisEshop, Icon: Globe, text: 'E-shop' },
                                { on: visKiosek, set: setVisKiosek, Icon: Tablet, text: 'Kiosek' },
                            ].map(({ on, set, Icon, text }) => (
                                <button key={text} type="button" onClick={() => set(v => !v)}
                                    className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-left"
                                    style={{ background: fieldBg, border: `1px solid ${on ? ACCENT : border}`, cursor: 'pointer' }}>
                                    <Icon size={16} style={{ color: on ? ACCENT : muted, flexShrink: 0 }} />
                                    <span className="text-sm flex-1" style={{ color: 'var(--text-primary)' }}>{text}</span>
                                    <span style={{ width: 34, height: 20, borderRadius: 999, flexShrink: 0, position: 'relative', transition: 'background 0.15s',
                                        background: on ? ACCENT : (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)') }}>
                                        <span style={{ position: 'absolute', top: 2, left: on ? 16 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                                    </span>
                                </button>
                            ))}
                        </div>
                        <div className="text-[11px] mt-2" style={{ color: muted }}>
                            {(!visPokladna && !visEshop && !visKiosek)
                                ? 'Jen v adminu (receptáři) — nezobrazí se v pokladně, na e-shopu ani na kiosku.'
                                : 'Admin vidí kategorii vždy. Skrytá nadřazená kategorie skryje i tuto.'}
                        </div>
                    </div>
                    )}

                    {/* Kuchyňská stanice — jen když jich provoz má víc než jednu. */}
                    {zobrazitViditelnost && stanice.length > 1 && (
                    <div className="mt-5">
                        {label('Kuchyňská stanice')}
                        <div className="flex flex-wrap gap-1.5">
                            {[{ id: null, nazev: 'Zdědit z nadřazené' }, ...stanice].map(s => {
                                const on = kdsStanice === s.id;
                                return (
                                    <button key={s.id ?? 'zdedit'} type="button" onClick={() => setKdsStanice(s.id)}
                                        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium"
                                        style={{ background: on ? 'rgba(245,158,11,0.15)' : fieldBg, border: `1px solid ${on ? ACCENT : border}`, color: on ? ACCENT : 'var(--text-primary)', cursor: 'pointer' }}>
                                        {s.id ? <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.barva || muted }} /> : <ChefHat size={12} />}
                                        {s.nazev}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="text-[11px] mt-2" style={{ color: muted }}>
                            Na kterém kuchyňském displeji se položky kategorie zobrazí. Podkategorie a položky to dědí, u položky jde přebít.
                        </div>
                    </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 p-4" style={{ borderTop: `1px solid ${border}` }}>
                    <div className="flex items-center gap-1">
                        {isEdit && (
                            <>
                                <button onClick={handleDelete}
                                    className="flex items-center gap-1 text-xs font-medium px-2.5 py-2 rounded-lg"
                                    style={{ background: 'none', border: `1px solid ${border}`, color: '#EF4444', cursor: 'pointer' }}>
                                    <Trash2 size={13} /> Smazat
                                </button>
                                <button onClick={() => onAddSub(node)}
                                    className="flex items-center gap-1 text-xs font-medium px-2.5 py-2 rounded-lg"
                                    style={{ background: 'none', border: `1px solid ${border}`, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                                    <FolderPlus size={13} /> Podkategorie
                                </button>
                            </>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="text-xs px-3 py-2"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted }}>Zrušit</button>
                        <button onClick={submit} disabled={!canSave}
                            className="flex items-center gap-1 text-xs font-semibold px-3.5 py-2 rounded-lg"
                            style={{ background: canSave ? ACCENT : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'),
                                color: canSave ? '#412402' : muted, border: 'none', cursor: canSave ? 'pointer' : 'not-allowed' }}>
                            <Check size={14} /> {isEdit ? 'Uložit' : 'Vytvořit'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
